/**
 * Shared layout component for admin create/edit form pages.
 *
 * Renders the common structure:
 * - Breadcrumb navigation
 * - Page title (derived from entity name and mode)
 * - Submit error alert (dismissible)
 * - Loading spinner (edit mode, while fetching)
 * - Fetch error alert (edit mode, on failure)
 * - EntityForm with Alert, Fields, and Actions
 */

import { EntityForm } from '@components/integrated/entity-form';
import { Text } from '@components/base/display';
import { VStack } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Spinner, Alert } from '@components/base/feedback';
import type { UseAdminFormPageReturn } from '../hooks/useAdminFormPage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  /** Display label for the breadcrumb. */
  label: string;
  /** Href for the breadcrumb link. Omit for the current (last) item. */
  href?: string;
}

export interface AdminFormPageProps {
  /**
   * The return value from useAdminFormPage.
   */
  formPage: UseAdminFormPageReturn;

  /**
   * Human-readable entity name for the page title.
   * Used as "Create {entityLabel}" or "Edit {entityLabel}".
   */
  entityLabel: string;

  /**
   * Breadcrumb items leading up to the current page.
   * The last item is automatically marked as current.
   */
  breadcrumbs: BreadcrumbItem[];

  /**
   * Breadcrumb items to show in the fetch error state.
   * Typically a subset ending with "Edit".
   * If not provided, falls back to the main breadcrumbs.
   */
  errorBreadcrumbs?: BreadcrumbItem[];

  /**
   * Generic error message for fetch failures when the error is not an Error instance.
   * Defaults to "An unexpected error occurred while loading the {entityName}."
   */
  genericFetchErrorMessage?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminFormPage({
  formPage,
  entityLabel,
  breadcrumbs,
  errorBreadcrumbs,
  genericFetchErrorMessage,
}: Readonly<AdminFormPageProps>) {
  const {
    isEdit,
    config,
    isFetchLoading,
    fetchError,
    submitError,
    setSubmitError,
    patchedForm,
  } = formPage;

  const entityNameLower = entityLabel.toLowerCase();
  const defaultFetchErrorMessage = `An unexpected error occurred while loading the ${entityNameLower}.`;
  const fetchErrorMessage = genericFetchErrorMessage ?? defaultFetchErrorMessage;

  // ---- Render: loading state (edit mode) -----------------------------------

  if (isEdit && isFetchLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading {entityNameLower}...</Text>
      </VStack>
    );
  }

  // ---- Render: fetch error (edit mode) -------------------------------------

  if (isEdit && fetchError) {
    const errBreadcrumbs = errorBreadcrumbs ?? breadcrumbs;

    return (
      <VStack gap={6}>
        <Breadcrumb>
          {errBreadcrumbs.map((item, index) => (
            <Breadcrumb.Item
              key={item.label}
              href={index < errBreadcrumbs.length - 1 ? item.href : undefined}
              isCurrent={index === errBreadcrumbs.length - 1}
            >
              {item.label}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <Alert variant="error" title={`Failed to load ${entityNameLower}`}>
          {fetchError instanceof Error
            ? fetchError.message
            : fetchErrorMessage}
        </Alert>
      </VStack>
    );
  }

  // ---- Render: main --------------------------------------------------------

  return (
    <VStack gap={6}>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        {breadcrumbs.map((item, index) => (
          <Breadcrumb.Item
            key={item.label}
            href={index < breadcrumbs.length - 1 ? item.href : undefined}
            isCurrent={index === breadcrumbs.length - 1}
          >
            {item.label}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>

      {/* Page title */}
      <Text as="h1" size="2xl" weight="bold">
        {isEdit ? `Edit ${entityLabel}` : `Create ${entityLabel}`}
      </Text>

      {/* Submit error alert */}
      {submitError && (
        <Alert
          variant="error"
          title="Save failed"
          dismissible
          onDismiss={() => { setSubmitError(null); }}
        >
          {submitError}
        </Alert>
      )}

      {/* Form */}
      <EntityForm config={config} form={patchedForm}>
        <EntityForm.Alert />
        <EntityForm.Fields />
        <EntityForm.Actions />
      </EntityForm>
    </VStack>
  );
}
